import {createSelector} from 'reselect';
import {Iterable} from 'immutable';
import {roundScore} from '@homeaway/react-review-components';

import {getListing, getReviews, getSystem, getTranslations, getTravelerFeatures} from './store';
import {getAverageRating, getBearStayReviewData, getBookingType, getDetailPageUrl} from './listing';
import {
    getDeviceSize,
    getIsMobile,
    getIsDesktop,
    getReviewPageSize,
    getLanguageNativeName,
    getLanguageName
} from './system';
import {labelMaker} from '../../server/utils/labelMaker';
import {isReviewsModalOpen} from './modalStates';

const REVIEW_COUNT_MIN = 5;

const VERIFIED_BRAND_NAMES = {
    vrbo: 'Vrbo',
    expedia: 'Expedia'
};

const formatReviews = (reviewList, hasReportReviewLink) => {
    return reviewList.map((review) => {
        const {isReportReviewActive = true} = review;
        return {
            ...review,
            ...(hasReportReviewLink ? {isReportReviewActive} : {})
        };
    });
};

// we determine isTranslationCandidate for each "page",
// so a user is not a candidate for the translations test
// if they would not have seen any non-site language reviews
const isTranslationCandidate = (reviewList) => {
    return reviewList.some((review) => {
        return review.isNonSiteLanguageReview;
    });
};

// this is a little kludgy, but we determine whether to show the error on each translation
// for either all of the reviews on a "page" or none of them
// the idea being usually we query for translations a page at a time.
// this breaks down on infinite scroll, or when re-trying translations,
// and is not represented well in the ui; it deserves to be re-visited
const hasTranslationError = (reviewList, isTranslationToggleOn, errorTranslatingReviews) => {
    if (isTranslationToggleOn && errorTranslatingReviews) {
        return reviewList.some((review) => {
            return review.isNonSiteLanguageReview && review.translated === false;
        });
    }

    return false;
};

const getJsReviews = createSelector(
    [getReviews],
    (reviewsData) => {
        return Iterable.isIterable(reviewsData) ? reviewsData.toJS() : reviewsData;
    }
);

const getBearStayBannerLabels = createSelector(
    [getTranslations],
    (trans) => {
        return {
            title: trans.bearStayNewTitle,
            description: trans.bearStayTeaserMessage,
            readReview: trans.bearStayTeaserButton
        };
    }
);

const getBearStayReview = createSelector(
    [getTranslations, getBearStayReviewData, getIsDesktop, getDeviceSize],
    (trans, bearStayReview, isDesktop, deviceSize) => {
        if (!bearStayReview) {
            return null;
        }

        let date = bearStayReview.stayedDate;
        const dateObj = new Date(date.split('T')[0].replace(/-/g, '/'));
        const splittedDate = dateObj.toDateString().split(' ');
        date = `${splittedDate[1]} ${splittedDate[2]}, ${splittedDate[3]}`;
        const content = bearStayReview.stayedComments;

        return {
            labels: {
                content,
                date,
                more: 'more',
                footerText: trans.bearStayTheBear,
                title: trans.bearStayReviewTitle,
                tooltipLabelText: trans.bearStayHelpLink
            },
            tooltipOptions: {
                delayShow: isDesktop,
                tooltipCentered: true,
                tooltipContent: trans.bearStayHelpContent,
                tooltipLabelText: trans.bearStayHelpLink,
                tooltipPlacement: isDesktop || deviceSize === 'md' ? 'left' : 'top',
                tooltipType: 'tooltip--lg'
            }
        };
    }
);

const getSuperlative = createSelector(
    [getAverageRating, getTranslations],
    (averageRating, translations) => {
        const RATING_EXCEPTIONAL = 5.0;
        const RATING_WONDERFUL = 4.8;
        const RATING_EXCELLENT = 4.3;
        const RATING_VERYGOOD = 4.0;
        const RATING_GOOD = 3.7;
        const scaled = roundScore(averageRating);
        let superlative = '';

        // Determine superlative (e.g. "Wonderful!")
        if (scaled === RATING_EXCEPTIONAL) {
            superlative = translations.superlativeExceptional;
        } else if (scaled >= RATING_WONDERFUL) {
            superlative = translations.superlativeWonderful;
        } else if (scaled >= RATING_EXCELLENT) {
            superlative = translations.superlativeExcellent;
        } else if (scaled >= RATING_VERYGOOD) {
            superlative = translations.superlativeVeryGood;
        } else if (scaled >= RATING_GOOD) {
            superlative = translations.superlativeGood;
        }

        return superlative;
    }
);

const getReviewsLabels = createSelector(
    [getJsReviews, getTranslations, getLanguageNativeName],
    (reviews, trans, languageNativeName) => {
        const viewAllReviews = reviews.reviewCount
            ? labelMaker(trans.viewAllReviews, reviews.reviewCount)
            : '';
        const seeAllReviews = reviews.reviewCount
            ? labelMaker(trans.seeAllReviews, reviews.reviewCount)
            : '';
        const postTranslationInfo = labelMaker(trans.postTranslationInfo, languageNativeName);

        const translateTo = labelMaker(trans.translateTo, languageNativeName);
        const labels = {
            nextPageOfReviews: trans.nextPageOfReviews,
            previousPageOfReviews: trans.previousPageOfReviews,
            reviewsSectionTitle: labelMaker(trans.reviewCount, {
                REVIEWCOUNT: reviews.reviewCount
            }),
            ownershipTransferred: trans.ownershipTransferred,
            sortBy: trans.sortBy,
            sortByRecentMost: trans.sortByRecentMost,
            sortByRecentLeast: trans.sortByRecentLeast,
            pagesize: trans.pagesize,
            latestReviews: trans.latestReviews,
            tenByPage: trans.tenByPage,
            twentyByPage: trans.twentyByPage,
            fiftyByPage: trans.fiftyByPage,
            of: trans.of,
            emptyReviews: trans.emptyReviews,
            by: trans.by,
            verifiedStay: trans.verifiedStay,
            submitted: trans.submitted,
            stayed: trans.stayed,
            source: trans.source,
            isHelpful: trans.isHelpful,
            yes: trans.yes,
            no: trans.no,
            more: trans.viewMore,
            less: trans.viewLess,
            readMore: trans.viewMore,
            ownersResponse: trans.ownersResponse,
            viewAllReviews,
            seeAllReviews,
            reportReviewActive: trans.reviewsReportLinkActive,
            reportReviewInactive: trans.reviewsReportLinkInactive,
            reviewsLegalDisclaimer: trans.reviewsLegalDisclaimer,
            skipToReviews: trans.skipToReviews,
            skipToReviewsAnchour: trans.skipToReviewsAnchour,
            translateTo,
            preTranslationInfo: trans.preTranslationInfo,
            postTranslationInfo,
            showOriginalReviews: trans.showOriginalReviews,
            translationErrorMsg: trans.translationErrorMsg,
            verifiedVrboLabel: labelMaker(trans.verifiedBrandLabel, {
                brand: VERIFIED_BRAND_NAMES.vrbo
            }),
            verifiedExpediaLabel: labelMaker(trans.verifiedBrandLabel, {
                brand: VERIFIED_BRAND_NAMES.expedia
            }),
            verifiedIpmLabel: trans.verifiedIpmLabel
        };
        return labels;
    }
);

const getReviewList = createSelector(
    [getJsReviews, getTravelerFeatures],
    (reviewsData, travelerFeatures) => {
        return formatReviews(reviewsData.reviews, travelerFeatures.hasReportReviewLink);
    }
);

const getTranslatedReviewList = createSelector(
    [getJsReviews],
    (reviewsData) => {
        const {reviews, translatedReviewsByUuid} = reviewsData;

        // create an array that mirrors review list,
        // but it may be a mixture of translated and untranslated reviews
        // based on whether we've translated every page and/or a review requires translation
        const translatedReviewList = reviews.map((review) => {
            if (translatedReviewsByUuid[review.uuid]) {
                return {
                    ...translatedReviewsByUuid[review.uuid],
                    translated: true
                };
            }

            return {...review, translated: false};
        });

        return formatReviews(translatedReviewList);
    }
);

const getNumReviewsToShow = createSelector(
    [getIsMobile, getReviewPageSize, isReviewsModalOpen],
    (isMobile, reviewPageSize, reviewsModalOpen) => {
        const MOBILE_PREVIEW_COUNT = 3;

        if (isMobile && !reviewsModalOpen) {
            return MOBILE_PREVIEW_COUNT;
        }

        return reviewPageSize;
    }
);

const getPaginationInfo = createSelector(
    [getJsReviews, getNumReviewsToShow],
    (reviewsData, numReviewsToShow) => {
        const {
            pageIndex: page,
            reviewCount: totalReviewCount,
            prevPageIndex: prevPage
        } = reviewsData;

        const pageSize = numReviewsToShow;
        const pageStartIndex = (page - 1) * pageSize;
        const pageEndIndex =
            page * pageSize <= totalReviewCount ? page * pageSize : totalReviewCount;
        const reviewsStart = pageStartIndex === 0;
        const reviewsEnd = pageEndIndex === totalReviewCount;

        return {
            page,
            pageSize,
            prevPage,
            totalReviewCount,
            pageStartIndex,
            pageEndIndex,
            reviewsStart,
            reviewsEnd
        };
    }
);

const getReviewSummaryData = createSelector(
    [getJsReviews],
    (reviewsData) => {
        // Translate the data received from the API to the contract
        // our components need
        const {averageRating} = reviewsData;

        return {
            score: averageRating
        };
    }
);

const getWriteReviewLinkData = createSelector(
    [getBookingType, getDetailPageUrl, getTranslations, getTravelerFeatures],
    (bookingType, detailPageUrl, trans, travelerFeatures) => {
        if (!bookingType.payPerBooking && travelerFeatures.hasWriteReviews) {
            const splitDetailPageUrl = detailPageUrl.split('/');
            const index = splitDetailPageUrl.length === 2 ? 1 : 2;
            splitDetailPageUrl.splice(index, 0, 'reviews/write');

            return {
                href: splitDetailPageUrl.join('/'),
                label: trans.writeReview
            };
        }

        return null;
    }
);

const getIsTranslationToggleOn = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.isTranslationToggleOn;
    }
);

const getErrorTranslatingReviews = createSelector(
    [getJsReviews],
    (reviewData) => {
        return reviewData.errorTranslatingReviews;
    }
);

const getTranslationButtonText = createSelector(
    [getJsReviews, getReviewsLabels, getIsTranslationToggleOn],
    (reviewsData, labels, isTranslationToggleOn) => {
        return !isTranslationToggleOn ? labels.translateTo : labels.showOriginalReviews;
    }
);

// Single page = Desktop
const getReviewDataForSinglePage = createSelector(
    [
        getErrorTranslatingReviews,
        getIsTranslationToggleOn,
        getPaginationInfo,
        getReviewList,
        getTranslatedReviewList
    ],
    (
        errorTranslatingReviews,
        isTranslationToggleOn,
        paginationInfo,
        reviewList,
        translatedReviewList
    ) => {
        const {pageStartIndex, pageEndIndex, pageSize} = paginationInfo;

        let reviewListSubset = reviewList.slice(pageStartIndex, pageEndIndex);
        let translatedReviewListSubset = translatedReviewList.slice(pageStartIndex, pageEndIndex);

        // if we've just paginated to a new page, but apollo has not yet added the reviews for that page to the store,
        // then continue to show the previous page reviews (behind the loading state) until store gets updated
        // this shouldn't be a problem when paging backwards on untranslated reviews, because the previous page will have already been populated
        if (!reviewListSubset.length) {
            reviewListSubset = reviewList.slice(
                Math.max(pageStartIndex - pageSize, 0),
                Math.max(pageEndIndex - pageSize, pageSize)
            );
        }

        // for translated reviews, it is possible to page backward and not have translated reviews to show
        // however, we fall back to untranslated reviews anyway and show the loading state while fetching translations
        if (!translatedReviewListSubset.length) {
            translatedReviewListSubset = translatedReviewList.slice(
                Math.max(pageStartIndex - pageSize, 0),
                Math.max(pageEndIndex - pageSize, pageSize)
            );
        }

        const reviewListForSinglePage = isTranslationToggleOn
            ? translatedReviewListSubset
            : reviewListSubset;
        const reviewListForSinglePageIsTranslationCandidate = isTranslationCandidate(
            reviewListSubset
        );
        const reviewListForSinglePageHasTranslationError = hasTranslationError(
            translatedReviewListSubset,
            isTranslationToggleOn,
            errorTranslatingReviews
        );

        return {
            reviewListForSinglePage,
            reviewListForSinglePageIsTranslationCandidate,
            reviewListForSinglePageHasTranslationError
        };
    }
);

// preview = mobile main page (non-modal)
const getReviewDataForPreview = createSelector(
    [
        getErrorTranslatingReviews,
        getIsTranslationToggleOn,
        getNumReviewsToShow,
        getReviewList,
        getTranslatedReviewList
    ],
    (
        errorTranslatingReviews,
        isTranslationToggleOn,
        numReviewsToShow,
        reviewList,
        translatedReviewList
    ) => {
        const reviewListSubset = reviewList.slice(0, numReviewsToShow);
        const translatedReviewListSubset = translatedReviewList.slice(0, numReviewsToShow);

        const reviewListForPreview = isTranslationToggleOn
            ? translatedReviewListSubset
            : reviewListSubset;
        const reviewListForPreviewIsTranslationCandidate = isTranslationCandidate(reviewListSubset);
        const reviewListForPreviewHasTranslationError = hasTranslationError(
            translatedReviewListSubset,
            isTranslationToggleOn,
            errorTranslatingReviews
        );

        return {
            reviewListForPreview,
            reviewListForPreviewIsTranslationCandidate,
            reviewListForPreviewHasTranslationError
        };
    }
);

// infinite scroll = mobile modal
const getReviewDataForInfiniteScroll = createSelector(
    [getErrorTranslatingReviews, getIsTranslationToggleOn, getReviewList, getTranslatedReviewList],
    (errorTranslatingReviews, isTranslationToggleOn, reviewList, translatedReviewList) => {
        const reviewListForInfiniteScroll = isTranslationToggleOn
            ? translatedReviewList
            : reviewList;
        const reviewListForInfiniteScrollIsTranslationCandidate = isTranslationCandidate(
            reviewList
        );
        const reviewListForInfiniteScrollHasTranslationError = hasTranslationError(
            translatedReviewList,
            isTranslationToggleOn,
            errorTranslatingReviews
        );

        return {
            reviewListForInfiniteScroll,
            reviewListForInfiniteScrollIsTranslationCandidate,
            reviewListForInfiniteScrollHasTranslationError
        };
    }
);

const getReviewsQueryVariables = createSelector(
    [getListing, getPaginationInfo],
    (listing, paginationInfo) => {
        const isInitial = false; // Inital call is server-side
        const listingId = listing.listingId;
        const {page, pageSize} = paginationInfo;

        return {isInitial, listingId, page, pageSize};
    }
);

/* Verified badges AB Testing */
const getShowVerifiedBadges = createSelector(
    [getJsReviews],
    ({reviewCount}) => {
        return reviewCount > 0;
    }
);

const getTravelerAppBrand = createSelector(
    [getSystem],
    (system) => {
        return system.travelerAppBrand;
    }
);

const getReviewsSelector = createSelector(
    [
        getReviewsLabels,
        getReviewSummaryData,
        getJsReviews,
        getReviewDataForInfiniteScroll,
        getReviewDataForPreview,
        getReviewDataForSinglePage,
        getShowVerifiedBadges,
        getPaginationInfo,
        getNumReviewsToShow,
        getIsMobile,
        getBearStayReview,
        getTravelerFeatures,
        getWriteReviewLinkData,
        getIsTranslationToggleOn,
        getLanguageNativeName,
        getLanguageName,
        getSuperlative,
        getTranslationButtonText,
        getTravelerAppBrand
    ],
    (
        labels,
        reviewsSummaryData,
        reviewsData,
        reviewDataForInfiniteScroll,
        reviewDataForPreview,
        reviewDataForSinglePage,
        showVerifiedBadges,
        paginationInfo,
        numReviewsToShow,
        isMobile,
        bearStayReview,
        travelerFeatures,
        writeReviewLinkData,
        isTranslationToggleOn,
        languageNativeName,
        languageName,
        superlative,
        translationButtonText,
        travelerAppBrand
    ) => {
        const {
            page,
            pageSize,
            totalReviewCount,
            pageStartIndex,
            pageEndIndex,
            reviewsStart,
            reviewsEnd
        } = paginationInfo;
        const {isFetching, isFetchingTranslatedReviews} = reviewsData;
        const hasMoreReviews = numReviewsToShow < totalReviewCount;
        const showReportLinks = travelerFeatures.hasReportReviewLink;
        const showReviewsLegalDisclaimer = travelerFeatures.showReviewsLegalDisclaimer;

        const {
            reviewListForSinglePage,
            reviewListForSinglePageIsTranslationCandidate,
            reviewListForSinglePageHasTranslationError
        } = reviewDataForSinglePage;

        const {
            reviewListForPreview,
            reviewListForPreviewIsTranslationCandidate,
            reviewListForPreviewHasTranslationError
        } = reviewDataForPreview;

        const {
            reviewListForInfiniteScroll,
            reviewListForInfiniteScrollIsTranslationCandidate,
            reviewListForInfiniteScrollHasTranslationError
        } = reviewDataForInfiniteScroll;

        return {
            bearStayReview,
            hasMoreReviews,
            isFetching,
            isFetchingTranslatedReviews,
            isMobile,
            isTranslationToggleOn,
            labels,
            languageName,
            languageNativeName,
            page,
            pageEndIndex,
            pageSize,
            pageStartIndex: pageStartIndex + 1,
            reviewCountMin: REVIEW_COUNT_MIN,
            reviewListForInfiniteScroll,
            reviewListForInfiniteScrollHasTranslationError,
            reviewListForInfiniteScrollIsTranslationCandidate,
            reviewListForPreview,
            reviewListForPreviewHasTranslationError,
            reviewListForPreviewIsTranslationCandidate,
            reviewListForSinglePage,
            reviewListForSinglePageHasTranslationError,
            reviewListForSinglePageIsTranslationCandidate,
            reviewsEnd,
            reviewsStart,
            reviewsSummaryData,
            showReportLinks,
            showReviewsLegalDisclaimer,
            showVerifiedBadges,
            superlative,
            totalReviewCount,
            translationButtonText,
            travelerAppBrand,
            writeReviewLinkData
        };
    }
);

export default getReviewsSelector;
export {
    formatReviews,
    getBearStayBannerLabels,
    getBearStayReview,
    getErrorTranslatingReviews,
    getIsTranslationToggleOn,
    getJsReviews,
    getNumReviewsToShow,
    getPaginationInfo,
    getReviewDataForSinglePage,
    getReviewDataForPreview,
    getReviewDataForInfiniteScroll,
    getReviewList,
    getReviewsLabels,
    getReviewsQueryVariables,
    getReviewsSelector,
    getReviewSummaryData,
    getSuperlative,
    getTranslatedReviewList,
    getTranslationButtonText,
    getShowVerifiedBadges,
    getTravelerAppBrand,
    getWriteReviewLinkData,
    hasTranslationError,
    isTranslationCandidate
};
